import React, { useEffect, useState } from "react";
import { ThinTextField, ThinDropdown } from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

const nonFoodItemsList = [
  { key: "clothing", label: "Clothing" },
  { key: "medical", label: "Medical " },
  { key: "education", label: "Education" },
];

const ExpenditureNonFood = ({ householdNonFoodList = {}, DqaApproved, DqaRejected }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved || false);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected || false);

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    setIsDqaApproved(DqaApproved || false);
    setIsDqaRejected(DqaRejected || false);
  }, [DqaApproved, DqaRejected]);

  useEffect(() => {
    if (householdNonFoodList && Object.keys(householdNonFoodList).length > 0) {
      setFormData(householdNonFoodList);
    }
  }, [householdNonFoodList]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleChange = (key, field, value) => {
    const fieldKey = `${key}_${field}`;

    if (field === "none") {
      // When toggling "None" checkbox
      const isNoneChecked = value === 1;
      setFormData((prev) => {
        const updated = { ...prev, [fieldKey]: isNoneChecked ? 1 : 0 };

        if (isNoneChecked) {
          // Clear other fields when "None" is checked
          updated[`${key}_unit`] = "";
          updated[`${key}_paid_consumed`] = "0";
          updated[`${key}_unpaid_consumed`] = "0";
        }

        return updated;
      });

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${key}_unit`];
        delete newErrors[`${key}_paid_consumed`];
        delete newErrors[`${key}_unpaid_consumed`];
        return newErrors;
      });
    } else {
      // For dropdown/input fields
      const stringValue = String(value).trim();
      setFormData((prev) => {
        const updated = { ...prev, [fieldKey]: stringValue };

        // If paid_consumed changes to "0", clear unit field
        if (field === "paid_consumed" && stringValue === "0") {
          updated[`${key}_unit`] = "";
        }

        return updated;
      });

      // Clear error for this field
      setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
    }
  };

  const validate = () => {
    const e = {};
    const req = (v) => v !== null && v !== undefined && String(v).trim() !== "";

    nonFoodItemsList.forEach(({ key, label }) => {
      const isNoneChecked = formData?.[`${key}_none`] === 1;
      if (!isNoneChecked) {
        if (!req(formData?.[`${key}_paid_consumed`])) {
          e[`${key}_paid_consumed`] = `${label} paid & consumed is required.`;
        }
        if (!req(formData?.[`${key}_unpaid_consumed`])) {
          e[
            `${key}_unpaid_consumed`
          ] = `${label} unpaid & consumed is required.`;
        }
        if (
          !req(formData?.[`${key}_unit`]) &&
          String(formData?.[`${key}_paid_consumed`]) === "1"
        ) {
          e[`${key}_unit`] = `${label} unit is required.`;
        }
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!id) {
      toast.error("Household ID is missing");
      return;
    }

    if (!validate()) {
      return;
    }

    setLoading(true);

    const payload = {
      household_information_id: parseInt(id),
    };

    nonFoodItemsList.forEach(({ key }) => {
      payload[`${key}_none`] = formData?.[`${key}_none`] || 0;
      payload[`${key}_unit`] = formData?.[`${key}_unit`] || "";
      payload[`${key}_paid_consumed`] =
        formData?.[`${key}_paid_consumed`] || "0";
      payload[`${key}_unpaid_consumed`] =
        formData?.[`${key}_unpaid_consumed`] || "0";
    });

    try {
      const response = await fetchData(
        `household-nonfood-expenditure-update/${id}`,
        "PUT",
        payload,
        {},
        {},
        navigate,
        "/household",
        dispatch
      );

      if (response?.success) {
        toast.success("Non-food expenditure updated successfully!");
        if (response.data) setFormData(response.data);
      } else {
        toast.error(response?.message || "Failed to update expenditure");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while saving data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Non-Food Expenditure
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Non-Food Expenditure
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      <div className="space-y-8">
        {nonFoodItemsList.map(({ key, label }) => (
          <div key={key} className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">{`Expenditure on ${label}`}</h3>

            {/* None checkbox */}
            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData?.[`${key}_none`] === 1}
                  onChange={(e) =>
                    handleChange(key, "none", e.target.checked ? 1 : 0)
                  }
                  disabled={isDqaApproved || isDqaRejected}
                  className="rounded border-gray-300 text-teal-600 shadow-sm focus:border-teal-300 focus:ring focus:ring-teal-200 focus:ring-opacity-50"
                />
                <span className="text-sm text-gray-700">None</span>
              </label>
            </div>

            {/* Form fields */}
            {formData?.[`${key}_none`] !== 1 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Paid & Consumed */}
                <div>
                  <ThinDropdown
                    label="Paid & Consumed"
                    options={[
                      { label: "Yes", value: "1" },
                      { label: "No", value: "0" },
                    ]}
                    name={`${key}_paid_consumed`}
                    value={String(formData?.[`${key}_paid_consumed`] ?? "0")}
                    onChange={(e) =>
                      handleChange(key, "paid_consumed", e.target.value)
                    }
                    disabled={isDqaApproved || isDqaRejected}
                    required
                    error={errors[`${key}_paid_consumed`]}
                    helperText={errors[`${key}_paid_consumed`]}
                    className="mb-5"
                  />
                  {errors[`${key}_paid_consumed`] && (
                    <p className="text-[0.9rem] text-red-600 mt-2">
                      {errors[`${key}_paid_consumed`]}
                    </p>
                  )}
                </div>

                {/* Unpaid & Consumed */}
                <div>
                  <ThinDropdown
                    label="Unpaid & Consumed"
                    options={[
                      { label: "Yes", value: "1" },
                      { label: "No", value: "0" },
                    ]}
                    name={`${key}_unpaid_consumed`}
                    value={String(formData?.[`${key}_unpaid_consumed`] ?? "0")}
                    onChange={(e) =>
                      handleChange(key, "unpaid_consumed", e.target.value)
                    }
                    disabled={isDqaApproved || isDqaRejected}
                    required
                    error={errors[`${key}_unpaid_consumed`]}
                    helperText={errors[`${key}_unpaid_consumed`]}
                    className="mb-5"
                  />
                  {errors[`${key}_unpaid_consumed`] && (
                    <p className="text-[0.9rem] text-red-600 mt-2">
                      {errors[`${key}_unpaid_consumed`]}
                    </p>
                  )}
                </div>

                {/* Unit */}
                {String(formData?.[`${key}_paid_consumed`]) === "1" && (
                  <div>
                    <ThinTextField
                      label={`Expenditure amount on ${label}`}
                      type="text"
                      value={formData?.[`${key}_unit`] || ""}
                      onChange={(e) =>
                        handleChange(key, "unit", e.target.value)
                      }
                      disabled={isDqaApproved || isDqaRejected}
                      required
                      className="mt-1"
                    />
                    {errors[`${key}_unit`] && (
                      <p className="text-[0.9rem] text-red-600 mt-2">
                        {errors[`${key}_unit`]}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Update Button */}
      {!isDqaApproved && !isDqaRejected && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`${
              loading ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
            } text-white rounded-lg px-4 py-2 transition-colors`}
          >
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpenditureNonFood;